"use client";

import React, { useState } from 'react';
import { useVendors, VendorWithProfile } from '@/hooks/useVendors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/DataTable';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, Search, Building2, MapPin, Award } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function VendorManagementPage() {
  const { vendors, loading, searchVendors } = useVendors();
  const [selectedVendor, setSelectedVendor] = useState<VendorWithProfile | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = () => {
    if (searchTerm.trim()) {
      searchVendors(searchTerm);
    }
  };

  const vendorColumns = [
    {
      key: 'business_name' as keyof VendorWithProfile,
      label: 'Business Name',
      sortable: true,
      render: (value: any) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{value || 'Not specified'}</span>
        </div>
      ),
    },
    {
      key: 'city' as keyof VendorWithProfile,
      label: 'Location',
      render: (value: any, vendor: VendorWithProfile) => (
        <div className="flex items-center gap-1">
          <MapPin className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm">
            {[vendor.city, vendor.state].filter(Boolean).join(', ') || 'Not specified'}
          </span>
        </div>
      ),
    },
    {
      key: 'is_verified' as keyof VendorWithProfile,
      label: 'Status',
      render: (value: boolean, vendor: VendorWithProfile) => (
        <div className="flex items-center gap-2">
          {value ? (
            <Badge variant="default" className="gap-1">
              <Award className="h-3 w-3" />
              Verified
            </Badge>
          ) : (
            <Badge variant="outline">Unverified</Badge>
          )}
          {vendor.verification_level && (
            <Badge variant="secondary" className="text-xs">
              {vendor.verification_level}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'created_at' as keyof VendorWithProfile,
      label: 'Joined',
      sortable: true,
      render: (value: string) => (
        <span className="text-sm text-muted-foreground">
          {formatDistanceToNow(new Date(value), { addSuffix: true })}
        </span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading vendors...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Vendor Management</h1>
        <p className="text-muted-foreground mt-2">
          View and manage all vendor profiles in the system
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <Input
              placeholder="Search vendors by business name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Vendors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{vendors.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Verified</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {vendors.filter(v => v.is_verified).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Premium</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {vendors.filter(v => v.verification_level === 'premium').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {vendors.length > 0
                ? (vendors.reduce((sum, v) => sum + (v.average_rating || 0), 0) / vendors.length).toFixed(1)
                : 'N/A'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vendors Table */}
      <Card>
        <CardHeader>
          <CardTitle>Vendors</CardTitle>
          <CardDescription>
            All vendor profiles in the system
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={vendorColumns}
            data={vendors}
            actions={(vendor) => (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedVendor(vendor);
                  setShowDetailDialog(true);
                }}
              >
                <Eye className="h-4 w-4 mr-2" />
                View
              </Button>
            )}
          />
        </CardContent>
      </Card>

      {/* Vendor Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vendor Details</DialogTitle>
            <DialogDescription>
              Complete vendor profile information
            </DialogDescription>
          </DialogHeader>
          {selectedVendor && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Business Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="font-medium">Business Name:</span>{' '}
                    <span>{selectedVendor.organization_name || selectedVendor.business_name || 'Not specified'}</span>
                  </div>
                  <div>
                    <span className="font-medium">Description:</span>{' '}
                    <p className="mt-1 text-muted-foreground">
                      {selectedVendor.business_description || 'No description provided'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">Location:</span>{' '}
                    <span>
                      {[
                        selectedVendor.city,
                        selectedVendor.state,
                        selectedVendor.zip_code,
                        selectedVendor.country,
                      ]
                        .filter(Boolean)
                        .join(', ') || 'Not specified'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Phone:</span>{' '}
                    <span>{selectedVendor.phone_number || 'Not specified'}</span>
                  </div>
                  <div>
                    <span className="font-medium">Website:</span>{' '}
                    <span>{selectedVendor.website || 'Not specified'}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Verification & Performance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="font-medium">Verified:</span>{' '}
                    <Badge variant={selectedVendor.is_verified ? 'default' : 'outline'}>
                      {selectedVendor.is_verified ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <div>
                    <span className="font-medium">Verification Level:</span>{' '}
                    <Badge variant="secondary">
                      {selectedVendor.verification_level || 'None'}
                    </Badge>
                  </div>
                  <div>
                    <span className="font-medium">Average Rating:</span>{' '}
                    <span>{selectedVendor.average_rating?.toFixed(1) || 'No ratings'}</span>
                  </div>
                  <div>
                    <span className="font-medium">Jobs Completed:</span>{' '}
                    <span>{selectedVendor.total_jobs_completed || 0}</span>
                  </div>
                  <div>
                    <span className="font-medium">Completion Rate:</span>{' '}
                    <span>
                      {selectedVendor.completion_rate
                        ? `${(selectedVendor.completion_rate * 100).toFixed(0)}%`
                        : 'N/A'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Metadata</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="text-sm">
                    <span className="font-medium">Vendor ID:</span>{' '}
                    <code className="text-xs bg-muted px-2 py-1 rounded">{selectedVendor.id}</code>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">User ID:</span>{' '}
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {selectedVendor.user_id}
                    </code>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Created:</span>{' '}
                    <span>
                      {formatDistanceToNow(new Date(selectedVendor.created_at), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Updated:</span>{' '}
                    <span>
                      {formatDistanceToNow(new Date(selectedVendor.updated_at), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
